// Configuración de API según entorno
import { requestTracker } from '../utils/requestTracker';

const isDevelopment = process.env.NODE_ENV === 'development';

// Sistema de tracking de errores de conexión
const connectionErrorTracker = {
  hasError: false,
  errorCount: 0,
  lastErrorTime: 0,
  maxRetries: 3,
  retryDelay: 30000, // 30 segundos
  
  recordError() {
    this.hasError = true;
    this.errorCount += 1;
    this.lastErrorTime = Date.now();
  },
  
  reset() {
    this.hasError = false;
    this.errorCount = 0;
    this.lastErrorTime = 0;
  },
  
  shouldRetry() {
    if (!this.hasError) return true;
    
    const timeSinceLastError = Date.now() - this.lastErrorTime;
    
    // Si han pasado más de 30 segundos, permitir un reintento
    if (timeSinceLastError >= this.retryDelay) {
      this.errorCount = 0;
      this.hasError = false;
      return true;
    }
    
    // Si ya se intentó 3 veces, no reintentar
    if (this.errorCount >= this.maxRetries) {
      return false;
    }
    
    return true;
  },
  
  isConnectionError(error) {
    return error?.message?.includes('Failed to fetch') || 
           error?.message?.includes('ERR_CONNECTION_REFUSED') ||
           error?.message?.includes('NetworkError') ||
           error?.name === 'TypeError' ||
           error?.code === 'ECONNREFUSED';
  }
};

// En desarrollo: se permite usar REACT_APP_API_URL; en producción: forzar rutas relativas (sin puerto)
const RAW_API_BASE_URL = process.env.REACT_APP_API_URL || '';
export const API_BASE_URL = isDevelopment ? RAW_API_BASE_URL : '';

export const API_PREFIX = '/api';

// Función helper para construir URLs de API
export const getApiUrl = (endpoint) => {
  return `${API_BASE_URL}${API_PREFIX}${endpoint}`;
};

// Función helper para obtener credenciales del localStorage
const getStoredCredentials = () => {
  try {
    const saved = localStorage.getItem('auth_credentials');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
  }
  return null;
};

// Función helper para agregar autenticación básica a las peticiones fetch
export const fetchWithAuth = async (url, options = {}) => {
  const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const timestamp = new Date().toISOString();
  const method = options.method || 'GET';
  
  // Extraer el endpoint del URL para logging
  const endpoint = url.split('/api/')[1] || url;
  
  // Determinar si es una petición de popup (no debe bloquear el mapa)
  const isPopupRequest = options.skipTracking || 
                        url.includes('/trade-areas') && (url.includes('place_id') || url.includes('zip=') || url.includes('branch='));
  
  if (options.body) {
  }
  
  // Registrar petición activa solo si no es una petición de popup
  if (!isPopupRequest) {
    requestTracker.addRequest(requestId);
  }
  
  const startTime = Date.now();
  
  try {
    // Obtener credenciales del localStorage
    const credentials = getStoredCredentials();
    
    // Si no hay credenciales, hacer la petición sin autenticación (fallará en el servidor)
    if (!credentials) {
      const response = await fetch(url, options);
      const duration = Date.now() - startTime;
      
      // Si la petición fue exitosa, resetear el tracker de errores
      if (response.ok) {
        connectionErrorTracker.reset();
      }
      
      // Remover petición activa solo si se registró
      if (!isPopupRequest) {
        requestTracker.removeRequest(requestId);
      }
      return response;
    }
    
    // Crear token de autenticación en base64
    const token = btoa(`${credentials.username}:${credentials.password}`);
    
    // Agregar header de autenticación
    const headers = {
      'Authorization': `Basic ${token}`,
      ...options.headers
    };
    
    // Realizar petición con autenticación
    const response = await fetch(url, {
      ...options,
      headers
    });
    
    const duration = Date.now() - startTime;
    
    // Si la petición fue exitosa, resetear el tracker de errores
    if (response.ok) {
      connectionErrorTracker.reset();
    }
    
    // Remover petición activa solo si se registró
    if (!isPopupRequest) {
      requestTracker.removeRequest(requestId);
    }
    
    return response;
  } catch (error) {
    const duration = Date.now() - startTime;
    
    // Detectar errores de conexión
    if (connectionErrorTracker.isConnectionError(error)) {
      // Verificar si debemos intentar la petición
      if (!connectionErrorTracker.shouldRetry()) {
        const retryAfter = Math.ceil((connectionErrorTracker.retryDelay - (Date.now() - connectionErrorTracker.lastErrorTime)) / 1000);
        throw new Error(`Server unavailable. Retry in ${retryAfter} seconds.`);
      }
      
      // Registrar el error
      connectionErrorTracker.recordError();
    } else {
      // Si no es un error de conexión, resetear el contador
      connectionErrorTracker.reset();
    }
    
    // Remover petición activa incluso en caso de error solo si se registró
    if (!isPopupRequest) {
      requestTracker.removeRequest(requestId);
    }
    throw error;
  }
};

// Función para resetear el tracker cuando una petición es exitosa
export const resetConnectionErrorTracker = () => {
  connectionErrorTracker.reset();
};

// URLs específicas de endpoints
export const API_ENDPOINTS = {
  ZIPCODES: '/zipcodes',
  ZIPCODES_ALL: '/zipcodes-all',
  STATISTICS: '/statistics',
  STATISTICS_ADVANCED: '/statistics/advanced',
  STATISTICS_BRANCH: '/statistics/branch',
  STATISTICS_TOP_HISPANIC: '/statistics/top-hispanic',
  TOP_HISPANIC_AREAS: '/top-hispanic-areas',
  DMAS: '/dmas',
  ESTADOS: '/estados',
  REGIONS: '/regions',
  CERTAINTEED_STATES: '/certainteed-states',
  CERTAINTEED_DBAS: '/certainteed-dbas',
  CERTAINTEED_BRANCHES: '/certainteed-branches',
  CERTAINTEED_CITIES: '/certainteed-cities',
  CERTAINTEED_POINTS: '/certainteed-points',
  CERTAINTEED_TRADE_AREAS: '/certainteed-trade-areas',
  QXO_POINTS: '/qxo-points',
  QXO_TRADE_AREAS: '/qxo-trade-areas',
  QXO_BRANCHES: '/qxo-branches',
  QXO_CITIES: '/qxo-cities',
  QXO_DBAS: '/qxo-dbas',
  APOLLO_POINTS: '/apollo-points',
  APOLLO_POINT_DETAIL: '/apollo-points',
  APOLLO_DECISION_MAKER_IDS: '/apollo-decision-maker-ids',
  APOLLO_DBAS: '/apollo-dbas',
  APOLLO_CITIES: '/apollo-cities',
  APOLLO_IO_LABELS: '/apollo-io/labels',
  CRM_DIRECTORY: '/crm-directory',
  APOLLO_IO_PEOPLE_SEARCH: '/apollo-io/people-search',
  APOLLO_IO_PEOPLE_ENRICH: '/apollo-io/people-enrich',
  EXPORT_TOP_200: '/export/top-200-certainteed.csv'
};

// Exportar función para obtener URL completa
export const api = {
  zipcodes: () => getApiUrl(API_ENDPOINTS.ZIPCODES),
  zipcodesAll: () => getApiUrl(API_ENDPOINTS.ZIPCODES_ALL),
  statistics: () => getApiUrl(API_ENDPOINTS.STATISTICS),
  statisticsAdvanced: () => getApiUrl(API_ENDPOINTS.STATISTICS_ADVANCED),
  statisticsBranch: () => getApiUrl(API_ENDPOINTS.STATISTICS_BRANCH),
  statisticsTopHispanic: () => getApiUrl(API_ENDPOINTS.STATISTICS_TOP_HISPANIC),
  topHispanicAreas: () => getApiUrl(API_ENDPOINTS.TOP_HISPANIC_AREAS),
  dmas: () => getApiUrl(API_ENDPOINTS.DMAS),
  estados: () => getApiUrl(API_ENDPOINTS.ESTADOS),
  regions: () => getApiUrl(API_ENDPOINTS.REGIONS),
  certainteedStates: () => getApiUrl(API_ENDPOINTS.CERTAINTEED_STATES),
  certainteedDbas: () => getApiUrl(API_ENDPOINTS.CERTAINTEED_DBAS),
  certainteedBranches: () => getApiUrl(API_ENDPOINTS.CERTAINTEED_BRANCHES),
  certainteedCities: () => getApiUrl(API_ENDPOINTS.CERTAINTEED_CITIES),
  certainteedPoints: () => getApiUrl(API_ENDPOINTS.CERTAINTEED_POINTS),
  certainteedTradeAreas: () => getApiUrl(API_ENDPOINTS.CERTAINTEED_TRADE_AREAS),
  qxoPoints: () => getApiUrl(API_ENDPOINTS.QXO_POINTS),
  qxoTradeAreas: () => getApiUrl(API_ENDPOINTS.QXO_TRADE_AREAS),
  qxoBranches: () => getApiUrl(API_ENDPOINTS.QXO_BRANCHES),
  qxoCities: () => getApiUrl(API_ENDPOINTS.QXO_CITIES),
  qxoDbas: () => getApiUrl(API_ENDPOINTS.QXO_DBAS),
  apolloPoints: () => getApiUrl(API_ENDPOINTS.APOLLO_POINTS),
  apolloPointDetail: (id) => getApiUrl(`${API_ENDPOINTS.APOLLO_POINT_DETAIL}/${id}`),
  apolloDecisionMakerIds: () => getApiUrl(API_ENDPOINTS.APOLLO_DECISION_MAKER_IDS),
  apolloDbas: () => getApiUrl(API_ENDPOINTS.APOLLO_DBAS),
  apolloCities: () => getApiUrl(API_ENDPOINTS.APOLLO_CITIES),
  /** Listas (labels) en Apollo.io; query opcional: modality=contacts|accounts|emailer_campaigns */
  apolloIoLabels: (params) => {
    const q = params && Object.keys(params).length
      ? `?${new URLSearchParams(params).toString()}`
      : '';
    return getApiUrl(`${API_ENDPOINTS.APOLLO_IO_LABELS}${q}`);
  },
  crmDirectory: (mode, limit = 200) =>
    getApiUrl(`${API_ENDPOINTS.CRM_DIRECTORY}?mode=${encodeURIComponent(mode)}&limit=${limit}`),
  apolloIoPeopleSearch: () => getApiUrl(API_ENDPOINTS.APOLLO_IO_PEOPLE_SEARCH),
  apolloIoPeopleEnrich: () => getApiUrl(API_ENDPOINTS.APOLLO_IO_PEOPLE_ENRICH),
  exportTop200: () => getApiUrl(API_ENDPOINTS.EXPORT_TOP_200)
}; 